import React, { useMemo } from 'react';
import { Marker, Polyline, Popup } from 'react-leaflet';

import { dotIcon } from './icons';
import { parseRouteGeometry, splitRouteAtPosition } from './geo';

/**
 * Planned itinerary, cut at the truck's position.
 *
 * `route_geometry_json` is produced by another service and may simply not be
 * there: an absent, empty or unreadable geometry renders nothing at all rather
 * than an error or a straight line between two unrelated points.
 */
export default function RouteLayer({
  geometry,
  position = null,
  showEndpoints = true,
  originLabel = 'Départ',
  destinationLabel = 'Arrivée',
}) {
  const route = useMemo(() => parseRouteGeometry(geometry), [geometry]);
  const { travelled, remaining } = useMemo(
    () => splitRouteAtPosition(route, position),
    [route, position],
  );

  if (route.length < 2) return null;

  return (
    <>
      {remaining.length > 1 && (
        <Polyline
          positions={remaining}
          pathOptions={{
            color: 'hsl(var(--cat-route))',
            weight: 5,
            opacity: 0.7,
            // Dashed for the part still to drive: the two halves stay
            // distinguishable without relying on the hue difference alone.
            dashArray: '10 8',
            lineCap: 'round',
          }}
        />
      )}
      {travelled.length > 1 && (
        <Polyline
          positions={travelled}
          pathOptions={{
            color: 'hsl(var(--cat-done))',
            weight: 5,
            opacity: 0.95,
            lineCap: 'round',
          }}
        />
      )}
      {showEndpoints && (
        <>
          <Marker
            position={route[0]}
            icon={dotIcon({ color: 'hsl(var(--cat-done))', label: originLabel })}
          >
            <Popup>{originLabel}</Popup>
          </Marker>
          <Marker
            position={route[route.length - 1]}
            icon={dotIcon({ color: 'hsl(var(--flame))', label: destinationLabel })}
          >
            <Popup>{destinationLabel}</Popup>
          </Marker>
        </>
      )}
    </>
  );
}
